import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, UserCircle } from '@phosphor-icons/react';
import UserAssets from '../../../components/UserAssets';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { getAssetTeam, listProductClients } from '../api';
import { Detail, ROLE_BADGE } from '../ui';

/**
 * One team member — profile summary plus the assets attached to them.
 */
function MemberAssetsPage() {
  const { teamId, userId } = useParams();
  const [team, setTeam] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getAssetTeam(teamId)
      .then((t) => {
        setTeam(t);
        if (t.product_id) {
          listProductClients(t.product_id).then(setClients).catch(() => setClients([]));
        }
      })
      .catch((e) => setError(e.response?.data?.detail || 'Could not load team'))
      .finally(() => setLoading(false));
  }, [teamId]);

  const member = team?.members?.find((m) => m.user_id === userId);
  const memberClientNames = member
    ? (member.client_ids || []).map((cid) => clients.find((c) => c.client_id === cid)?.name).filter(Boolean)
    : [];

  return (
    <div data-testid="asset-member-page">
      <Link to={`/dashboard/assets/teams/${teamId}`} style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px',
        color: '#002FA7', textDecoration: 'none', marginBottom: '8px', fontWeight: '600',
      }}>
        <ArrowLeft size={14} /> {team?.name || 'Team'}
      </Link>

      {loading ? (
        <SkeletonBlock />
      ) : error || !member ? (
        <EmptyState
          icon={UserCircle}
          title="Member not found"
          description={error || 'This user is not on this team anymore.'}
        />
      ) : (
        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            {member.picture ? (
              <img src={member.picture} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <UserCircle size={44} color="#9CA3AF" />
            )}
            <div>
              <p style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{member.name}</p>
              <p style={{ fontSize: '12px', color: '#6B7280' }}>{member.designation || member.email}</p>
            </div>
            <span className="badge" style={{
              background: ROLE_BADGE[member.org_role] || '#F3F4F6',
              fontSize: '11px', textTransform: 'capitalize', marginLeft: 'auto',
            }}>
              {member.org_role}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '8px' }}>
            <Detail label="Email" value={member.email} />
            <Detail label="Designation" value={member.designation} />
            <Detail label="Team Role" value={member.org_role} capitalize />
            <Detail label="Product / Team" value={team.name} />
            {team.product_id && (
              <Detail label="Client(s)" value={memberClientNames.length ? memberClientNames.join(', ') : 'None (product-level)'} />
            )}
          </div>

          <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
            <UserAssets userId={member.user_id} editable />
          </div>
        </div>
      )}
    </div>
  );
}

export default MemberAssetsPage;
